// Agent IO catalog. The twelve launched roles live in the server library.
// Everything else here is a name: one practice applied to one domain.
// Promoting a name into the library is a separate step (original prompt,
// coordinator test). This file does not create prompts.

export const FAMILIES = [
  { id: 'inquiry', name: 'Inquiry', emoji: '🔍', shape: 'circle', line: 'Find the facts' },
  { id: 'product', name: 'Product', emoji: '🧭', shape: 'diamond', line: 'Decide what to build' },
  { id: 'build', name: 'Build', emoji: '🛠️', shape: 'square', line: 'Make the change' },
  { id: 'explain', name: 'Explain', emoji: '✍️', shape: 'bars', line: 'Say it in public' },
  { id: 'risk', name: 'Risk', emoji: '🛡️', shape: 'triangle', line: 'Hold the boundary' },
  { id: 'commercial', name: 'Commercial', emoji: '💼', shape: 'hex', line: 'Price and sell' },
  { id: 'operations', name: 'Operations', emoji: '⚙️', shape: 'ring', line: 'Keep it running' },
  { id: 'people', name: 'People', emoji: '👥', shape: 'pair', line: 'Staff the work' },
  { id: 'data', name: 'Data', emoji: '📊', shape: 'grid', line: 'Read the numbers' },
  { id: 'coordination', name: 'Coordination', emoji: '🎯', shape: 'cross', line: 'Name who goes' },
];

export const PRACTICE_EMOJI = {
  research: '🔎',
  analyst: '📈',
  extraction: '📄',
  discovery: '🗺️',
  comparison: '⚖️',
  interview: '🎤',
  spec: '📋',
  architecture: '🏗️',
  design: '🎨',
  roadmap: '🛣️',
  acceptance: '✅',
  engineering: '💻',
  reviewer: '👀',
  qa: '🧪',
  release: '🚀',
  migration: '📦',
  integration: '🔌',
  accessibility: '♿',
  content: '📝',
  editor: '✂️',
  documentation: '📚',
  localization: '🌐',
  security: '🔒',
  privacy: '🕶️',
  compliance: '📜',
  legal: '⚖️',
  audit: '🧾',
  sales: '🤝',
  pricing: '🏷️',
  finance: '💰',
  procurement: '🛒',
  partnerships: '🤲',
  enablement: '🎓',
  success: '🌟',
  support: '💬',
  ops: '📟',
  incident: '🚨',
  observability: '📡',
  capacity: '📏',
  scheduling: '🗓️',
  vendor: '🏭',
  maintenance: '🔧',
  recruiting: '🧲',
  onboarding: '🚪',
  training: '🏋️',
  data: '🗂️',
  forecasting: '📉',
  reporting: '📰',
  labeling: '🔖',
  coordinator: '🎯',
};

// live: true marks a role that already has a server prompt.
export const PRACTICES = [
  { id: 'research', name: 'Research', family: 'inquiry', live: true, blurb: 'Gathers the facts the objective depends on, and marks what is still unknown.' },
  { id: 'analyst', name: 'Analyst', family: 'inquiry', blurb: 'Turns those facts into the decision the objective needs.' },
  { id: 'extraction', name: 'Extraction', family: 'inquiry', blurb: 'Pulls the fields out of a document: parties, amounts, dates, terms.' },
  { id: 'discovery', name: 'Discovery', family: 'inquiry', blurb: 'Maps who is involved and what they already use.' },
  { id: 'comparison', name: 'Comparison', family: 'inquiry', blurb: 'Sets two options beside each other on the same criteria.' },
  { id: 'interview', name: 'Interview', family: 'inquiry', blurb: 'Drafts the questions that would close an open decision.' },

  { id: 'spec', name: 'Spec', family: 'product', live: true, blurb: 'Turns research into who it is for, what done means, and what is out of scope.' },
  { id: 'architecture', name: 'Architecture', family: 'product', live: true, blurb: 'Challenges the design: what breaks, what is already in the stack, and what should stay simple.' },
  { id: 'design', name: 'Design', family: 'product', blurb: 'Describes what the person sees and the order they do it in.' },
  { id: 'roadmap', name: 'Roadmap', family: 'product', blurb: 'Orders the work into the smallest sequence that reaches done.' },
  { id: 'acceptance', name: 'Acceptance', family: 'product', blurb: 'Writes the sentence that means this objective is finished.' },

  { id: 'engineering', name: 'Engineering', family: 'build', live: true, blurb: 'Writes the implementation plan: files, seams, and the check that proves it works.' },
  { id: 'reviewer', name: 'Reviewer', family: 'build', blurb: 'Reads the change and names what it actually does.' },
  { id: 'qa', name: 'QA', family: 'build', live: true, blurb: 'Names the cases that must pass, including the one that should fail closed.' },
  { id: 'release', name: 'Release', family: 'build', blurb: 'Lists what ships, what stays back, and how to tell it worked.' },
  { id: 'migration', name: 'Migration', family: 'build', blurb: 'Plans the move from the current record shape to the next one.' },
  { id: 'integration', name: 'Integration', family: 'build', blurb: 'Names the system on the other side and the field that has to match.' },
  { id: 'accessibility', name: 'Accessibility', family: 'build', blurb: 'Checks that the flow can be completed without the mouse and without color alone.' },

  { id: 'content', name: 'Content', family: 'explain', live: true, blurb: 'Writes the public explanation in the product voice.' },
  { id: 'editor', name: 'Editor', family: 'explain', blurb: 'Cuts the public sentence until it says one thing.' },
  { id: 'documentation', name: 'Documentation', family: 'explain', blurb: 'Writes the steps a new operator follows.' },
  { id: 'localization', name: 'Localization', family: 'explain', blurb: 'Lists the phrases that have to stay exact in another language.' },

  { id: 'security', name: 'Security', family: 'risk', live: true, blurb: 'Looks for a missing gate, a secret in the wrong place, or a path outside the declared boundary.' },
  { id: 'privacy', name: 'Privacy', family: 'risk', blurb: 'Names the personal data in the flow and who is allowed to see it.' },
  { id: 'compliance', name: 'Compliance', family: 'risk', live: true, blurb: 'Lists the evidence the run should keep: who decided, what was proposed, what was committed.' },
  { id: 'legal', name: 'Legal', family: 'risk', blurb: 'Reads a clause for what it obligates and what it leaves open.' },
  { id: 'audit', name: 'Audit', family: 'risk', blurb: 'Traces a claim back to the record that supports it.' },

  { id: 'sales', name: 'Sales', family: 'commercial', live: true, blurb: 'States the buyer, the finished job, and the next conversation.' },
  { id: 'pricing', name: 'Pricing', family: 'commercial', live: true, blurb: 'States what is included, what is an add-on, and what is not charged yet.' },
  { id: 'finance', name: 'Finance', family: 'commercial', live: true, blurb: 'Checks the meter: what this run spends, and what would make it stop.' },
  { id: 'procurement', name: 'Procurement', family: 'commercial', blurb: 'Compares the vendor, the term, and the exit.' },
  { id: 'partnerships', name: 'Partnerships', family: 'commercial', blurb: 'Names what each side keeps and what they share.' },
  { id: 'enablement', name: 'Enablement', family: 'commercial', blurb: 'Writes what the seller has to know before the next call.' },
  { id: 'success', name: 'Success', family: 'commercial', blurb: 'Names the outcome the customer was promised and whether it landed.' },
  { id: 'support', name: 'Support', family: 'commercial', blurb: 'Sorts an incoming request into the next owner and the missing fact.' },

  { id: 'ops', name: 'Ops', family: 'operations', blurb: 'Says what is running, what is late, and what to do next.' },
  { id: 'incident', name: 'Incident', family: 'operations', blurb: 'Orders the facts of an outage: start, scope, and the current workaround.' },
  { id: 'observability', name: 'Observability', family: 'operations', blurb: 'Names the signal that would show this job is healthy.' },
  { id: 'capacity', name: 'Capacity', family: 'operations', blurb: 'Estimates how much work the current shape can hold.' },
  { id: 'scheduling', name: 'Scheduling', family: 'operations', blurb: 'Places the work on a clock without double-booking the constraint.' },
  { id: 'vendor', name: 'Vendor', family: 'operations', blurb: 'Tracks what a supplier owes and what is still open.' },
  { id: 'maintenance', name: 'Maintenance', family: 'operations', blurb: 'Lists the upkeep that has to happen before the thing fails.' },

  { id: 'recruiting', name: 'Recruiting', family: 'people', blurb: 'Turns a seat into the skills the work actually needs.' },
  { id: 'onboarding', name: 'Onboarding', family: 'people', blurb: 'Lists what a new person has to be able to do in the first week.' },
  { id: 'training', name: 'Training', family: 'people', blurb: 'Breaks a skill into the practice that teaches it.' },

  { id: 'data', name: 'Data', family: 'data', blurb: 'Names the table, the grain, and the field the question depends on.' },
  { id: 'forecasting', name: 'Forecasting', family: 'data', blurb: 'States the assumption and the range, and keeps the point estimate honest.' },
  { id: 'reporting', name: 'Reporting', family: 'data', blurb: 'Picks the few numbers a reader needs and the period they cover.' },
  { id: 'labeling', name: 'Labeling', family: 'data', blurb: 'Writes the rule a person uses to mark one example.' },

  { id: 'coordinator', name: 'Coordinator', family: 'coordination', live: true, blurb: 'Picks the library roles for one objective and stops when the goal or the budget is met.' },
];

export const DOMAINS = [
  ['healthcare', 'Healthcare', '🏥'],
  ['hospital', 'Hospital', '🏨'],
  ['clinic', 'Clinic', '🩺'],
  ['pharmacy', 'Pharmacy', '💊'],
  ['insurance', 'Insurance', '☂️'],
  ['banking', 'Banking', '🏦'],
  ['payments', 'Payments', '💳'],
  ['wealth', 'Wealth', '💎'],
  ['lending', 'Lending', '🤝'],
  ['tax', 'Tax', '🧾'],
  ['retail', 'Retail', '🛍️'],
  ['ecommerce', 'Ecommerce', '🛒'],
  ['cpg', 'Consumer goods', '📦'],
  ['manufacturing', 'Manufacturing', '🏭'],
  ['logistics', 'Logistics', '🚚'],
  ['warehouse', 'Warehouse', '🏬'],
  ['energy', 'Energy', '⚡'],
  ['utilities', 'Utilities', '💡'],
  ['legal', 'Law firms', '⚖️'],
  ['education', 'Education', '🎓'],
  ['schools', 'Schools', '🏫'],
  ['higher-ed', 'Higher education', '🏛️'],
  ['government', 'Government', '🏢'],
  ['cities', 'Cities', '🏙️'],
  ['real-estate', 'Real estate', '🏠'],
  ['hospitality', 'Hospitality', '🛎️'],
  ['media', 'Media', '🎬'],
  ['publishing', 'Publishing', '📰'],
  ['telecom', 'Telecom', '📶'],
  ['agriculture', 'Agriculture', '🌾'],
  ['automotive', 'Automotive', '🚗'],
  ['aerospace', 'Aerospace', '✈️'],
  ['construction', 'Construction', '🏗️'],
  ['pharma', 'Pharma', '🧪'],
  ['nonprofit', 'Nonprofit', '🎗️'],
  ['sports', 'Sports', '🏆'],
  ['gaming', 'Gaming', '🎮'],
  ['climate', 'Climate', '🌍'],
  ['food', 'Food', '🍽️'],
  ['maritime', 'Maritime', '⚓'],
  ['aviation', 'Aviation', '🛫'],
  ['field-service', 'Field service', '🧰'],
  ['software', 'Software', '💻'],
  ['marketing', 'Marketing', '📣'],
  ['hr', 'Human resources', '🧑‍💼'],
  ['facilities', 'Facilities', '🚪'],
  ['mining', 'Mining', '⛏️'],
  ['water', 'Water', '💧'],
  ['waste', 'Waste', '♻️'],
  ['life-sciences', 'Life sciences', '🔬'],
].map(([id, name, emoji]) => ({ id, name, emoji }));

const FAMILY_NAME = Object.fromEntries(FAMILIES.map((f) => [f.id, f.name]));
const PRACTICE_BY_ID = Object.fromEntries(PRACTICES.map((p) => [p.id, p]));

export const CATALOG_PRACTICES = PRACTICES.filter((p) => p.id !== 'coordinator');
export const CATALOG_COUNT = CATALOG_PRACTICES.length * DOMAINS.length;

export function familyOfRole(roleId) {
  return PRACTICE_BY_ID[roleId]?.family || 'library';
}

export function practicesIn(familyId) {
  return PRACTICES.filter((p) => p.family === familyId);
}

// Search is practice × domain. Empty query returns null (browse mode).
export function searchCatalog(query) {
  const terms = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return null;
  const hits = [];
  for (const practice of CATALOG_PRACTICES) {
    for (const domain of DOMAINS) {
      const name = `${domain.name} ${practice.name}`;
      const hay = `${name} ${practice.id} ${domain.id} ${FAMILY_NAME[practice.family]}`.toLowerCase();
      if (terms.every((term) => hay.includes(term))) {
        hits.push({
          id: `${domain.id}.${practice.id}`,
          name,
          familyId: practice.family,
          family: FAMILY_NAME[practice.family],
          practiceId: practice.id,
          practice: practice.name,
          domainId: domain.id,
        });
      }
    }
  }
  return hits;
}
